import React from 'react'
import { Filters, IPaginationMeta, Sorters } from "app/modules/tp/_apiResponse";
import { Entity } from 'app/models/entity';
import { generateKey } from 'app/utils/keys';
import Select, { GroupBase, SingleValue } from 'react-select';
import { Option, Options } from '../form/SelectInputField';

export const defaultMetaPagination = {
    current_page: 1,
    last_page: 1,
    per_page: 25,
    total: 0
};

export type Column<T extends Entity,> = {
    key: string,
    label: string,
    className?: string,
    cellStyle?: (entity: T, column: Column<T>) => React.CSSProperties,
    filterable?: boolean,
    filterType?: string,
    filterOptions?: Options,
    filterOn?: string,
    sortable?: boolean,
    width?: string,
    render?: (value: T) => JSX.Element
}

// @TODO: change e to html event type
const onFilterChange = (e: any): string => {
    if (!e.target.value) {
        return "";
    }
    return e.target.value;
}


// @TODO: change e to html event type
const onSorterChange = (e: any): string => {
    if (!e.target.value) {
        return "";
    }
    return "";
}

const getHeaderStyle = <T extends Entity, R extends Entity>(column: Column<T>) => {
    if (column.width) {
        return { style: { width: column.width } }
    }
    return {};
}

const getHeaderClasses = <T extends Entity, R extends Entity>(column: Column<T>) => {
    let defaultClasses = ["min-w-200px", "h-100", "d-inline-flex", "flex-column", "justify-content-between", "flex-nowrap", "mb-2",];
    if (column.width) {
        defaultClasses = defaultClasses.filter((c) => c !== "min-w-200px");
    }
    return defaultClasses.join(" ");
}



const renderHeader = <T extends Entity, R extends Entity>({
    columns,
    pagination,
    filters,
    sorters,
    onChange,
}: TableProps<T, R>) => {

    function getFilterInputByType<T extends Entity>(column: Column<T>, columnCompleteKey: string) {
        switch (column?.filterType) {

            case 'select':
                return (<Select
                    placeholder="Nessun filtro"
                    value={column.filterOptions?.find((o) => o.value === filters[`${columnCompleteKey}[eq]`])}
                    options={
                        (column?.filterOptions ?? []) as any
                    }
                    onChange={
                        (e: any) => {
                            if(e?.value === "_"){
                                delete filters[`${columnCompleteKey}[eq]`]
                            } else if (e?.value !== "" && e?.value !== "" && e?.value !== undefined) {
                                filters = { ...filters, [`${columnCompleteKey}[eq]`]: e?.value }
                            }

                            onChange(pagination.current_page, { ...filters }, sorters)
                        }
                    }
                />)

            default:
                return (<input name={columnCompleteKey} className="form-control form-control-solid" style={{ height: "30px" }} type="text" value={filters[`${columnCompleteKey}[like]`] ?? ""}
                    onChange={(e) => onChange(pagination.current_page, { ...filters, [`${columnCompleteKey}[like]`]: onFilterChange(e) }, sorters)} />
                )

        }
    }

    return (
        <tr
            className="fw-bold fs-6 text-gray-800 border-bottom-2 border-gray-200">
            {columns
                .map((column: Column<T>) => {
                    const columnCompleteKey = column.filterOn ? `${column.key}[${column.filterOn}]` : column.key;
                    return (
                        <React.Fragment key={columnCompleteKey} >
                            <th>
                                <div
                                    {...getHeaderStyle(column)}
                                    className={getHeaderClasses(column)}>
                                    <div className='d-flex align-items-center'>
                                        {column.sortable &&
                                            <div className='d-flex flex-column me-2'
                                                onClick={() => onChange(pagination.current_page, filters,
                                                    ({
                                                        ...sorters,
                                                        [columnCompleteKey]: sorters[columnCompleteKey] === 'ASC' ? 'DESC' : (sorters[columnCompleteKey] === 'DESC' ? '' : 'ASC'),
                                                    })
                                                )}>
                                                <i className="fas fa-sort-up" style={{ marginBottom: "-5px", cursor: 'pointer', color: (sorters[columnCompleteKey] === 'ASC' ? 'black' : 'gray') }}></i>
                                                <i className="fas fa-sort-down" style={{ marginTop: "-5px", cursor: 'pointer', color: (sorters[columnCompleteKey] === 'DESC' ? 'black' : 'gray') }}></i>
                                            </div>
                                        }
                                        {column.label}
                                    </div>

                                    {column.filterable &&
                                        getFilterInputByType(column, columnCompleteKey)
                                    }
                                </div>
                            </th>
                        </React.Fragment>
                    )
                })}
        </tr>
    )
};

const renderBody = <T extends Entity, R extends Entity>({
    entities,
    columns,
    onRowClick,
}: TableProps<T, R>) => (
    entities?.length > 0 ? entities
        .map((entity: T) => {
            return (
                <tr className="tp-table-tr tp-table-tr-hover"
                    key={generateKey(entity)}
                    onClick={() => onRowClick(entity)}>
                    {
                        columns.map((column: Column<T>) => (
                            <td key={generateKey(entity)}>
                                <div className='tp-table-cell' >
                                    <div style={(column?.cellStyle && column.cellStyle(entity, column)) ?? {}}>
                                        {column.render ? column.render(entity) : entity[column.key]}
                                    </div>
                                </div>
                            </td>
                        ))
                    }
                </tr>
            )
        }) : (
        <tr className="tp-table-tr-hover" key={generateKey()} >
            <td key={generateKey()} colSpan={columns.length} className="text-center">Nessun elemento trovato</td>
        </tr>
    )
);

type OnTableChange = (page: number, filters: Filters, sorters: Sorters) => void;

type TableProps<T extends Entity, R extends Entity> = {
    entities: Array<T>,
    columns: Array<Column<T>>,
    pagination: IPaginationMeta,
    filters: Filters,
    sorters: Sorters,
    onChange: OnTableChange,
    onRowClick: (e: T) => void
    style?: React.CSSProperties
}

const Table = <T extends Entity, R extends Entity>(props: TableProps<T, R>) => {
    return (
        <>
            <div
                //key={generateKey(props.entities)}
                className="table-responsive">
                <table className="table table-striped gy-7 gs-7 tp-table">
                    <thead>
                        {renderHeader(props)}
                    </thead>
                    <tbody>
                        {renderBody(props)}
                    </tbody>
                </table>
            </div>
        </>
    )
}

export default Table;
